export const PAYMENT_METHODS = Object.freeze({
  TABBY: "Tabby",
  TAMARA: "Tamara",
  MOYASAR: "Moyasar",
});

export const PAYMENT_METHOD_FEE_RATES = Object.freeze({
  [PAYMENT_METHODS.TABBY]: 0.07,
  [PAYMENT_METHODS.TAMARA]: 0.07,
  [PAYMENT_METHODS.MOYASAR]: 0.0275,
});

export const MOYASAR_PAYMENT_ENDPOINT =
  "/api/method/gulf_payment.gulf_payment.doctype.moyasar_settings.moyasar_settings.create_moyasar_payment";

const BASE_BILLING_FIELDS = Object.freeze([
  "email",
  "name",
  "phone",
  "city",
  "address",
  "zip",
]);

export const PAYMENT_BILLING_FIELD_LABELS = Object.freeze({
  order_id: "Quotation ID",
  email: "Email",
  name: "Name",
  phone: "Phone",
  city: "City",
  address: "Address",
  zip: "ZIP Code",
  state: "State",
  country: "Country",
});

export const isRedirectPaymentMethod = (method) =>
  method === PAYMENT_METHODS.MOYASAR;

export const getRequiredBillingFieldsForPaymentMethod = (method) =>
  method === PAYMENT_METHODS.MOYASAR
    ? []
    : [...BASE_BILLING_FIELDS];

export const getPaymentMethodFeeRate = (method) =>
  PAYMENT_METHOD_FEE_RATES[method] || 0;

export const roundPaymentAmount = (amount) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return 0;
  return Math.round((numericAmount + Number.EPSILON) * 100) / 100;
};

export const calculatePaymentMethodFee = (amount, method) => {
  return roundPaymentAmount(Number(amount) * getPaymentMethodFeeRate(method));
};

export const calculatePaymentMethodTotal = (amount, method) => {
  return roundPaymentAmount(Number(amount) + calculatePaymentMethodFee(amount, method));
};

export const buildPaymentPricing = (amount, method) => {
  const baseAmount = roundPaymentAmount(amount);
  const feeRate = getPaymentMethodFeeRate(method);
  const feeAmount = calculatePaymentMethodFee(baseAmount, method);

  return {
    baseAmount,
    feeRate,
    feePercentage: roundPaymentAmount(feeRate * 100),
    feeAmount,
    totalAmount: roundPaymentAmount(baseAmount + feeAmount),
  };
};

const normalizePayloadValue = (value) => String(value ?? "").trim();

export const buildMoyasarPaymentPayload = ({
  orderId,
  amount,
  billingInfo = {},
}) => ({
  order_id: normalizePayloadValue(orderId),
  amount: Number(amount),
  email: normalizePayloadValue(billingInfo.email),
  name: normalizePayloadValue(billingInfo.name),
  phone: normalizePayloadValue(billingInfo.phone),
  city: normalizePayloadValue(billingInfo.city),
  address: normalizePayloadValue(billingInfo.address),
  zip: normalizePayloadValue(billingInfo.zip),
});

export const getMissingMoyasarPaymentFields = (payload) => {
  const missingFields = [];

  if (!payload?.order_id) {
    missingFields.push(PAYMENT_BILLING_FIELD_LABELS.order_id);
  }

  if (!Number.isFinite(payload?.amount) || payload.amount <= 0) {
    missingFields.push("Payment Amount");
  }

  return missingFields;
};

const normalizeUrlCandidate = (value) => {
  if (typeof value !== "string") return "";

  const trimmedValue = value.trim();
  return /^https?:\/\//i.test(trimmedValue) || trimmedValue.startsWith("/")
    ? trimmedValue
    : "";
};

export const resolvePaymentRedirectUrl = (response) => {
  const candidates = [
    response?.data?.checkout_url,
    response?.data?.payment_url,
    response?.data?.payment_request_url,
    response?.data?.result?.checkout_url,
    response?.data?.result?.payment_url,
    response?.data?.result?.payment_request_url,
    response?.data?.message?.checkout_url,
    response?.data?.message?.payment_url,
    response?.data?.message?.payment_request_url,
    response?.checkout_url,
    response?.payment_url,
    response?.payment_request_url,
    response?.result?.checkout_url,
    response?.result?.payment_url,
    response?.result?.payment_request_url,
    response?.message?.checkout_url,
    response?.message?.payment_url,
    response?.message?.payment_request_url,
    response?.data?.url,
    response?.data?.result?.url,
    response?.data?.message?.url,
    response?.url,
    response?.result?.url,
    response?.message?.url,
    response?.data?.message,
    response?.data?.result,
    response?.message,
    response?.result,
  ];

  return candidates.map(normalizeUrlCandidate).find(Boolean) || "";
};

export const resolvePaymentLocale = (language) =>
  String(language || "")
    .trim()
    .toLowerCase()
    .startsWith("ar")
    ? "ar_SA"
    : "en_US";
